import React from 'react';

import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
} from 'react-router-dom';

// ============================================================
// ZENIMONIES BANKING
// APPLICATION ROUTES
// PERSONAL + BUSINESS BANKING
// ============================================================

// ==================== AUTHENTICATION ====================

import Login from './pages/Login.tsx';
import CustomerCareLogin from './pages/CustomerCareLogin.tsx';
import Register from './pages/Register.tsx';
import ForgotPassword from './pages/ForgotPassword.tsx';
import ResetPassword from './pages/ResetPassword.tsx';

// ==================== DASHBOARDS ====================

import Dashboard from './pages/Dashboard.tsx';
import BusinessDashboard from './pages/BusinessDashboard.tsx';
import Business from './pages/Business.tsx';

// ==================== ACCOUNT ====================

import Profile from './pages/Profile.tsx';
import Settings from './pages/Settings.tsx';
import KYC from './pages/KYC.tsx';
import VerifyPhone from './pages/VerifyPhone.tsx';
import VerifyOTP from './pages/VerifyOTP.tsx';

// ==================== SECURITY ====================

import PasskeySecurity from './pages/PasskeySecurity.tsx';
import AccountLocked from './pages/AccountLocked.tsx';

// ==================== MONEY ====================

import Transfer from './pages/Transfer.tsx';
import ToBank from './pages/ToBank.tsx';
import Deposit from './pages/Deposit.tsx';
import Withdraw from './pages/Withdraw.tsx';
import Transactions from './pages/Transactions.tsx';
import TransferConfirmation from './pages/TransferConfirmation.tsx';
import TransactionReceipt from './pages/TransactionReceipt.tsx';
import Statement from './pages/Statement.tsx';
import BusinessTransactions from './pages/BusinessTransactions.tsx';
import Beneficiaries from './pages/Beneficiaries.tsx';
import Wallet from './pages/Wallet.tsx';

// ==================== PAYMENTS ====================

import Airtime from './pages/Airtime.tsx';
import Data from './pages/Data.tsx';
import OtherBills from './pages/OtherBills.tsx';
import GiftCards from './pages/GiftCards.tsx';
import TVSubscription from './pages/TVSubscription.tsx';
import Bills from './pages/Bills.tsx';
import InternetBills from './pages/InternetBills.tsx';
import Electricity from './pages/Electricity.tsx';
import ElectricityVerification from './pages/ElectricityVerification.tsx';
import ElectricityPaymentConfirmation from './pages/ElectricityPaymentConfirmation.tsx';
import Betting from './pages/Betting.tsx';
import Education from './pages/Education.tsx';
import Insurance from './pages/Insurance.tsx';

// ==================== OTHER SERVICES ====================

import VirtualCard from './pages/VirtualCard.tsx';
import Savings from './pages/Savings.tsx';
import Notifications from './pages/Notifications.tsx';
import BusinessServicePage from './pages/BusinessServicePage.tsx';
import HelpCenter from './pages/HelpCenter.tsx';
import Support from './pages/Support.tsx';
import ThemeProvider from './theme/Theme.tsx';

// ==================== ADMIN ====================

import AdminLogin from './pages/AdminLogin.tsx';
import Admin from './pages/AdminDashboard.tsx';
import AirtimeReconciliation from './pages/AirtimeReconciliation.tsx';

// ==================== CUSTOMER CARE ====================

import CustomerCareDashboard from './pages/CustomerCareDashboard.tsx';

// ==================== SESSION ====================

import SessionGuard from './components/SessionGuard.tsx';


class AdminErrorBoundary extends React.Component<
  React.PropsWithChildren,
  { hasError: boolean; message: string }
> {
  state = { hasError: false, message: '' };

  static getDerivedStateFromError(error: unknown) {
    return {
      hasError: true,
      message:
        error instanceof Error
          ? error.message
          : 'Unexpected admin dashboard error.',
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(
      'ZENIMONIES admin dashboard render error:',
      error,
      info
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: 24,
            margin: 20,
            background: '#ffffff',
            color: '#173b2d',
            border: '1px solid #dce9e2',
            borderRadius: 12,
            fontFamily: 'Arial, sans-serif',
          }}
        >
          <h2>ZENIMONIES Admin Dashboard</h2>
          <p>The dashboard encountered a display error.</p>
          <p
            style={{
              padding: 12,
              background: '#fff4f2',
              color: '#9b2c1c',
              overflowWrap: 'anywhere',
            }}
          >
            <strong>Error details:</strong> {this.state.message}
          </p>
          <button onClick={() => window.location.reload()}>
            Reload dashboard
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================
// ADMIN ROUTE GUARD
// ============================================================

const AdminRoute: React.FC = () => {
  const adminToken =
    localStorage.getItem('adminToken');

  if (!adminToken) {
    return (
      <Navigate
        to="/admin/login"
        replace
      />
    );
  }

   return (
    <AdminErrorBoundary>
      <Admin />
    </AdminErrorBoundary>
  );
};

// ============================================================
// GENERAL SERVICE PAGE
// USED BY PERSONAL BANKING
// ============================================================

interface ServicePageProps {
  title: string;
  description: string;
  icon: string;
}

const ServicePage: React.FC<ServicePageProps> = ({
  title,
  description,
  icon,
}) => {
  const location =
    useLocation();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f6faf8',
        color: '#172b22',
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
        paddingBottom: '40px',
      }}
    >
      <header
        style={{
          background: '#ffffff',
          borderBottom:
            '1px solid #e5ebe8',
          position: 'sticky',
          top: 0,
          zIndex: 20,
        }}
      >
        <div
          style={{
            width:
              'min(920px, 92%)',
            margin: '0 auto',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent:
              'space-between',
          }}
        >
          <Link
            to="/"
            style={{
              textDecoration:
                'none',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '11px',
                background:
                  'linear-gradient(135deg, #079447, #007a3f)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent:
                  'center',
                fontSize: '20px',
                fontWeight: 800,
              }}
            >
              Z
            </div>

            <div>
              <div
                style={{
                  fontSize: '17px',
                  fontWeight: 800,
                  color: '#063b2d',
                }}
              >
                Zenimonies
              </div>

              <div
                style={{
                  fontSize: '9px',
                  letterSpacing:
                    '1.5px',
                  color: '#98a2a0',
                }}
              >
                DIGITAL BANKING
              </div>
            </div>
          </Link>

          <Link
            to="/"
            style={{
              textDecoration:
                'none',
              color: '#087c43',
              fontWeight: 700,
              fontSize: '13px',
            }}
          >
            Home
          </Link>
        </div>
      </header>

      <main
        style={{
          width:
            'min(700px, 92%)',
          margin: '0 auto',
          paddingTop: '28px',
        }}
      >
        <Link
          to="/"
          style={{
            display:
              'inline-flex',
            alignItems:
              'center',
            gap: '6px',
            textDecoration:
              'none',
            color: '#66756e',
            fontSize: '13px',
            fontWeight: 600,
            marginBottom:
              '18px',
          }}
        >
          ← Back to Dashboard
        </Link>

        <div
          style={{
            background:
              '#ffffff',
            border:
              '1px solid #e5ebe8',
            borderRadius: '20px',
            padding: '26px',
            boxShadow:
              '0 8px 25px rgba(26, 61, 47, 0.05)',
          }}
        >
          <div
            style={{
              width: '58px',
              height: '58px',
              borderRadius:
                '17px',
              background:
                '#e8f8f0',
              color: '#087c43',
              display: 'flex',
              alignItems:
                'center',
              justifyContent:
                'center',
              fontSize: '27px',
              marginBottom:
                '17px',
            }}
          >
            {icon}
          </div>

          <h1
            style={{
              margin:
                '0 0 8px',
              fontSize: '26px',
              fontWeight: 800,
              color: '#063b2d',
            }}
          >
            {title}
          </h1>

          <p
            style={{
              margin:
                '0 0 20px',
              color: '#66756e',
              fontSize: '14px',
              lineHeight: 1.6,
            }}
          >
            {description}
          </p>

          <div
            style={{
              padding: '15px',
              borderRadius:
                '13px',
              background:
                '#effbf5',
              border:
                '1px solid #d2eee0',
              color: '#05603a',
              fontSize: '13px',
              lineHeight: 1.5,
            }}
          >
            This service is ready for
            the next setup stage.
          </div>

          <div
            style={{
              marginTop: '20px',
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <Link
              to="/"
              style={{
                textDecoration:
                  'none',
                background:
                  '#079447',
                color:
                  '#ffffff',
                padding:
                  '11px 17px',
                borderRadius:
                  '10px',
                fontWeight: 700,
                fontSize:
                  '13px',
              }}
            >
              Back Home
            </Link>

            <button
              type="button"
              onClick={() =>
                window.history.back()
              }
              style={{
                background:
                  '#ffffff',
                border:
                  '1px solid #d0d9d5',
                color:
                  '#344054',
                padding:
                  '11px 17px',
                borderRadius:
                  '10px',
                fontWeight: 700,
                fontSize:
                  '13px',
                cursor:
                  'pointer',
              }}
            >
              Go Back
            </button>
          </div>
        </div>

        <div
          style={{
            marginTop:
              '13px',
            color:
              '#98a2b3',
            fontSize:
              '11px',
            textAlign:
              'center',
          }}
        >
          {location.pathname}
        </div>
      </main>
    </div>
  );
};

// ============================================================
// ADMIN ROUTES
//
// IMPORTANT:
// SessionGuard is NOT mounted here.
// ============================================================

const AdminRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/admin/login"
        element={
          <AdminLogin />
        }
      />

      <Route
        path="/admin"
        element={
          <Navigate
            to="/admin/dashboard"
            replace
          />
        }
      />

      <Route
        path="/admin/dashboard"
        element={
          <AdminRoute />
        }
      />

      <Route
        path="*"
        element={
          <Navigate
            to="/admin/login"
            replace
          />
        }
      />
    </Routes>
  );
};

// ============================================================
// CUSTOMER CARE ROUTE GUARD
//
// CUSTOMER CARE IS A SEPARATE WORKSPACE.
//
// Allowed:
//   role = customer_care
//
// Not allowed:
//   user
//   admin
//
// The backend customerCareMiddleware is the REAL security
// boundary. This frontend guard only controls navigation.
// ============================================================

const CustomerCareRoute: React.FC = () => {
  let user: any = null;

  try {
    const storedUser =
      localStorage.getItem(
        'zenimonies_user'
      );

    if (storedUser) {
      user =
        JSON.parse(
          storedUser
        );
    }
  } catch (error) {
    console.error(
      'Unable to read stored Customer Care user:',
      error
    );
  }

  const role =
    String(
      user?.role || ''
    )
      .trim()
      .toLowerCase();

  console.log(
    'ZENIMONIES Customer Care route check:',
    {
      role,
      user,
    }
  );

  // ----------------------------------------------------------
  // CUSTOMER CARE AGENT
  // ----------------------------------------------------------

  if (
    role === 'customer_care'
  ) {
    return (
      <CustomerCareDashboard />
    );
  }

  // ----------------------------------------------------------
  // ADMIN
  // ----------------------------------------------------------

  if (
    role === 'admin'
  ) {
    return (
      <Navigate
        to="/admin/dashboard"
        replace
      />
    );
  }

  // ----------------------------------------------------------
  // NORMAL CUSTOMER / UNKNOWN USER
  // ----------------------------------------------------------

  return (
    <Navigate
      to="/customer-care/login"
      replace
    />
  );
};

// ============================================================
// CUSTOMER CARE ROUTES
//
// IMPORTANT:
// Customer Care Login is NOT inside SessionGuard.
//
// Customer Care Workspace is also NOT inside SessionGuard.
//
// This prevents the normal banking session guard from
// redirecting Customer Care agents to the normal dashboard.
// ============================================================

const CustomerCareRoutes: React.FC = () => {
  return (
    <Routes>
      {/* ======================================================
          CUSTOMER CARE LOGIN
          ====================================================== */}

      <Route
        path="/customer-care/login"
        element={
          <CustomerCareLogin />
        }
      />

      {/* ======================================================
          CUSTOMER CARE WORKSPACE
          ====================================================== */}

      <Route
        path="/customer-care"
        element={
          <CustomerCareRoute />
        }
      />

      {/* ======================================================
          CUSTOMER CARE UNKNOWN URL
          ====================================================== */}

      <Route
        path="*"
        element={
          <Navigate
            to="/customer-care/login"
            replace
          />
        }
      />
    </Routes>
  );
};

// ============================================================
// CUSTOMER ROUTES
// ============================================================

const CustomerRoutes: React.FC = () => {
  return (
    <SessionGuard>
      <Routes>

        {/* ================= AUTHENTICATION ================= */}

        <Route
          path="/login"
          element={
            <Login />
          }
        />

        <Route
          path="/register"
          element={
            <Register />
          }
        />

        <Route
          path="/forgot-password"
          element={
            <ForgotPassword />
          }
        />

        <Route
          path="/reset-password"
          element={
            <ResetPassword />
          }
        />

        {/* ================= SECURITY ================= */}

        <Route
          path="/passkey-security"
          element={
            <PasskeySecurity />
          }
        />

        <Route
          path="/account-locked"
          element={
            <AccountLocked />
          }
        />

        {/* ================= PERSONAL DASHBOARD ================= */}

        <Route
          path="/"
          element={
            <Dashboard />
          }
        />

        <Route
          path="/dashboard"
          element={
            <Navigate
              to="/"
              replace
            />
          }
        />

        {/* ================= BUSINESS BANKING ================= */}

        <Route
          path="/business/dashboard/:id"
          element={
            <BusinessDashboard />
          }
        />

        <Route
          path="/business/:id/transactions"
          element={
            <BusinessTransactions />
          }
        />

        <Route
          path="/business/:id/:section"
          element={
            <BusinessServicePage />
          }
        />

        <Route
          path="/business/:id"
          element={
            <Navigate
              to="/business"
              replace
            />
          }
        />

        <Route
          path="/business"
          element={
            <Business />
          }
        />

        {/* ================= NOTIFICATIONS ================= */}

        <Route
          path="/notifications"
          element={
            <Notifications />
          }
        />

        {/* ================= HELP CENTER ================= */}

        <Route
          path="/help-center"
          element={
            <HelpCenter />
          }
        />

        {/* ================= CUSTOMER SUPPORT ================= */}

        <Route
          path="/support"
          element={
            <Support />
          }
        />

        {/* ================= ACCOUNT ================= */}

        <Route
          path="/profile"
          element={
            <Profile />
          }
        />

        <Route
          path="/settings"
          element={
            <Settings />
          }
        />

        <Route
          path="/kyc"
          element={
            <KYC />
          }
        />

        <Route
          path="/verify-phone"
          element={
            <VerifyPhone />
          }
        />

        <Route
          path="/verify-otp"
          element={
            <VerifyOTP />
          }
        />

        {/* ================= MONEY ================= */}

        <Route
          path="/transfer"
          element={
            <Transfer />
          }
        />

        <Route
          path="/to-bank"
          element={
            <ToBank />
          }
        />

        <Route
          path="/transfer-confirmation"
          element={
            <TransferConfirmation />
          }
        />

        <Route
          path="/deposit"
          element={
            <Deposit />
          }
        />

        <Route
          path="/withdraw"
          element={
            <Withdraw />
          }
        />

        <Route
          path="/transactions"
          element={
            <Transactions />
          }
        />

        <Route
          path="/transaction-receipt"
          element={
            <TransactionReceipt />
          }
        />

        <Route
          path="/statement"
          element={
            <Statement />
          }
        />

        <Route
          path="/beneficiaries"
          element={
            <Beneficiaries />
          }
        />

        {/* ================= WALLET ================= */}

        <Route
          path="/wallet"
          element={
            <Wallet />
          }
        />

        {/* ================= VIRTUAL CARD ================= */}

        <Route
          path="/virtual-card"
          element={
            <VirtualCard />
          }
        />

        {/* ================= AIRTIME ================= */}

        <Route
          path="/airtime"
          element={
            <Airtime />
          }
        />

        {/* ================= DATA ================= */}

        <Route
          path="/data"
          element={
            <Data />
          }
        />

        {/* ================= TV ================= */}

        <Route
          path="/tv-subscription"
          element={
            <TVSubscription />
          }
        />

        <Route
          path="/tv"
          element={
            <TVSubscription />
          }
        />

        {/* ================= BILLS ================= */}

        <Route
          path="/bills"
          element={
            <Bills />
          }
        />

        <Route
          path="/bills/internet"
          element={
            <InternetBills />
          }
        />

        <Route
          path="/bills/other"
          element={
            <OtherBills />
          }
        />

        {/* ================= GIFT CARDS ================= */}

        <Route
          path="/gift-cards"
          element={
            <GiftCards />
          }
        />

        {/* ================= ELECTRICITY ================= */}

        <Route
          path="/electricity"
          element={
            <Electricity />
          }
        />

        <Route
          path="/electricity/verification"
          element={
            <ElectricityVerification />
          }
        />

        <Route
          path="/electricity/payment-confirmation"
          element={
            <ElectricityPaymentConfirmation />
          }
        />

        {/* ================= EDUCATION ================= */}

        <Route
          path="/education"
          element={
            <Education />
          }
        />

        {/* ================= INSURANCE ================= */}

        <Route
          path="/insurance"
          element={
            <Insurance />
          }
        />

        {/* ================= BETTING ================= */}

        <Route
          path="/betting"
          element={
            <Betting />
          }
        />

        {/* ================= SAVINGS ================= */}

        <Route
          path="/safebox"
          element={
            <ServicePage
              title="SafeBox"
              icon="◉"
              description="Manage funds you want to keep separately from your available balance."
            />
          }
        />

        <Route
          path="/savings"
          element={
            <Savings />
          }
        />

        {/* ================= MORE ================= */}

        <Route
          path="/more"
          element={
            <ServicePage
              title="More Services"
              icon="••"
              description="Explore additional Zenimonies services and account features."
            />
          }
        />

        {/* ================= AIRTIME RECONCILIATION ================= */}

        <Route
          path="/airtime-reconciliation"
          element={
            <AirtimeReconciliation />
          }
        />

        {/* ================= FALLBACK ================= */}

        <Route
          path="*"
          element={
            <Navigate
              to="/"
              replace
            />
          }
        />

      </Routes>
    </SessionGuard>
  );
};

// ============================================================
// ROUTER SWITCH
//
// /admin/*
//     → AdminRoutes
//
// /customer-care/*
//     → CustomerCareRoutes
//
// everything else
//     → CustomerRoutes
//
// This is the important separation.
// ============================================================

const AppRouter: React.FC = () => {
  const location =
    useLocation();

  const pathname =
    location.pathname;

  // ==========================================================
  // ADMIN WORKSPACE
  // ==========================================================

  const isAdminRoute =
    pathname === '/admin' ||
    pathname.startsWith(
      '/admin/'
    );

  if (isAdminRoute) {
    return (
      <AdminRoutes />
    );
  }

  // ==========================================================
  // CUSTOMER CARE WORKSPACE
  //
  // IMPORTANT:
  // This check happens BEFORE CustomerRoutes.
  //
  // Therefore SessionGuard cannot redirect the Customer Care
  // login or Customer Care dashboard to the normal dashboard.
  // ==========================================================

  const isCustomerCareRoute =
    pathname ===
      '/customer-care' ||
    pathname.startsWith(
      '/customer-care/'
    );

  if (
    isCustomerCareRoute
  ) {
    return (
      <CustomerCareRoutes />
    );
  }

  // ==========================================================
  // NORMAL CUSTOMER BANKING
  // ==========================================================

  return (
    <CustomerRoutes />
  );
};

// ============================================================
// MAIN APPLICATION
// ============================================================

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
